import React, { useEffect, useState } from 'react';
import { Drawer, Box, Stack, Typography, IconButton, Avatar, CircularProgress, Alert, Button } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import http, { apiErrorMessage } from '../../../Api/http';
import { GetSalaryBreakdownByEmployeeCode } from '../../../Api/Api';
import { inr, initialsFromName, avatarTone } from '../../../utils/format';
import { passphraseConfig, isPassphraseError, passphraseErrorText, clearSalaryPassphrase, useSalaryPassphrase } from '../../../utils/salaryPassphrase';
import { PRIMARY_DARK, PRIMARY_LIGHT } from '../../../theme';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// The register's month is a PAYOUT month (YYYY-MM) that settles the previous month's work
// (payroll runs in arrears). The breakdown endpoint wants the WORK month as MM-yyyy.
export const workMonthFromPayout = (payoutMonth) => {
    const [y, m] = String(payoutMonth || '').split('-').map(Number);
    if (!y || !m) return null;
    const d = new Date(y, m - 2, 1);
    return {
        param: `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`,
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
        payoutLabel: `${MONTH_NAMES[m - 1]} ${y}`,
    };
};

const humanize = (k) => String(k)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isNum = (v) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));

// Accepts either an array of { name/label, amount } or a plain { key: amount } object.
const toLines = (v) => {
    if (Array.isArray(v)) {
        return v.map((it, i) => ({
            label: it?.name ?? it?.label ?? it?.title ?? it?.component ?? it?.type ?? `Item ${i + 1}`,
            amount: it?.amount ?? it?.value ?? it?.total ?? 0,
        }));
    }
    if (v && typeof v === 'object') {
        return Object.entries(v).filter(([, val]) => isNum(val)).map(([k, val]) => ({ label: humanize(k), amount: val }));
    }
    return [];
};

const pick = (obj, keys) => {
    for (const k of keys) if (obj && obj[k] != null && typeof obj[k] !== 'object') return obj[k];
    return null;
};

// Whatever fields the server sent for the section; falls back to a flat scan of known key names.
const EARNING_KEYS = ['basic', 'basicSalary', 'hra', 'da', 'transportAllowance', 'transport', 'specialAllowance', 'special', 'incentive', 'bonus', 'incentiveBonus', 'additionalSalary', 'overtime', 'overtimePay'];
const DEDUCTION_KEYS = ['pf', 'providentFund', 'esi', 'pt', 'professionalTax', 'tds', 'otherDeductions', 'lop', 'lopDeduction'];

const flatLines = (d, keys) => keys.filter((k) => isNum(d?.[k])).map((k) => ({ label: humanize(k), amount: d[k] }));

const pairs = (obj) => Object.entries(obj || {}).filter(([, v]) => v != null && typeof v !== 'object').map(([k, v]) => ({ label: humanize(k), value: String(v) }));

function Lines({ title, lines, tone, emptyText }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#6E6B99', letterSpacing: 0.6, textTransform: 'uppercase', mb: 0.8 }}>{title}</Typography>
            <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F1F0F9', borderRadius: '9px', px: 1.6, py: 0.4 }}>
                {lines.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: '#98A0AE', py: 1 }}>{emptyText}</Typography>
                ) : lines.map((l, i) => (
                    <Stack key={`${l.label}-${i}`} direction="row" sx={{ justifyContent: 'space-between', py: 0.9, borderBottom: i < lines.length - 1 ? '1px solid #F1F0F9' : 'none' }}>
                        <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{l.label}</Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone }}>{inr(l.amount)}</Typography>
                    </Stack>
                ))}
            </Box>
        </Box>
    );
}

// `unlock` is the parent screen's useSalaryUnlock(): the breakdown needs the Encryption Password on an encrypted entity.
export default function SalaryBreakdownDrawer({ open, employee, payoutMonth, onClose, unlock }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [needsPassphrase, setNeedsPassphrase] = useState(false);
    const passphrase = useSalaryPassphrase();
    const work = workMonthFromPayout(payoutMonth);
    const code = employee?.rollNumber || employee?.employeeCode || '';

    useEffect(() => {
        if (!open || !code || !work) return undefined;
        let cancelled = false;
        setLoading(true); setError(''); setNeedsPassphrase(false); setData(null);
        http.get(GetSalaryBreakdownByEmployeeCode, passphraseConfig(undefined, { params: { employeeCode: code, month: work.param } }))
            .then(({ data: body }) => {
                if (cancelled) return;
                if (body?.error) throw new Error(body.message || 'Could not load the salary breakdown.');
                setData(body?.data ?? body ?? {});
            })
            .catch((err) => {
                if (cancelled) return;
                if (isPassphraseError(err)) {
                    // Strict endpoint: no locked fallback, so ask for the passphrase and retry once it is entered.
                    if (err.response.status === 401) clearSalaryPassphrase();
                    setNeedsPassphrase(err.response.status !== 423);
                    setError(passphraseErrorText(err));
                    return;
                }
                setError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load the salary breakdown.') : err.message);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, code, payoutMonth, passphrase]);

    const d = data || {};
    const earnings = toLines(d.earnings ?? d.earningLines ?? d.earningItems);
    const deductions = toLines(d.deductions ?? d.statutoryDeductions ?? d.deductionLines ?? d.deductionItems);
    const earningLines = earnings.length ? earnings : flatLines(d, EARNING_KEYS);
    const deductionLines = deductions.length ? deductions : flatLines(d, DEDUCTION_KEYS);
    const attendance = pairs(d.attendance);
    const gross = pick(d, ['grossSalary', 'gross', 'totalGross', 'totalEarnings']);
    const totalDed = pick(d, ['totalDeductions', 'deductionsTotal']);
    const net = pick(d, ['netSalary', 'netPay', 'net', 'netPayable', 'totalNet']);
    const name = employee?.name || employee?.employeeName || code;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, borderTopLeftRadius: '14px', borderBottomLeftRadius: '14px' } } }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.4, px: 2.4, py: 2, borderBottom: '1px solid #F1F0F9' }}>
                <Avatar sx={{ width: 44, height: 44, ...avatarTone(name), fontSize: 15, fontWeight: 700 }}>{initialsFromName(name)}</Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }} noWrap>{name}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }} noWrap>
                        {code}{employee?.designation ? ` · ${employee.designation}` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Stack>

            <Box sx={{ px: 2.4, py: 2, flex: 1, overflowY: 'auto' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>Salary breakdown</Typography>
                <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 2 }}>
                    {work ? `Work month ${work.label} — paid out in ${work.payoutLabel} (payroll runs in arrears)` : 'Select a payout month'}
                </Typography>

                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>}
                {error && (
                    <Alert
                        severity={needsPassphrase ? 'warning' : 'error'} sx={{ borderRadius: '9px', fontSize: 12.5 }}
                        action={needsPassphrase && unlock?.canUnlock ? (
                            <Button size="small" onClick={() => unlock.requestUnlock()} sx={{ textTransform: 'none', fontWeight: 700 }}>Unlock</Button>
                        ) : null}
                    >
                        {error}{needsPassphrase && unlock && !unlock.canUnlock ? ' Your role is not allowed to unlock salary figures.' : ''}
                    </Alert>
                )}

                {data && !loading && (
                    <>
                        <Lines title="Earnings" lines={earningLines} tone="#16A34A" emptyText="No earnings lines returned." />
                        <Lines title="Deductions" lines={deductionLines} tone="#DC2626" emptyText="No deductions for this month." />

                        {attendance.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#6E6B99', letterSpacing: 0.6, textTransform: 'uppercase', mb: 0.8 }}>Attendance</Typography>
                                <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F1F0F9', borderRadius: '9px', px: 1.6, py: 0.4 }}>
                                    {attendance.map((a, i) => (
                                        <Stack key={a.label} direction="row" sx={{ justifyContent: 'space-between', py: 0.9, borderBottom: i < attendance.length - 1 ? '1px solid #F1F0F9' : 'none' }}>
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{a.label}</Typography>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }}>{a.value}</Typography>
                                        </Stack>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ bgcolor: PRIMARY_LIGHT, borderRadius: '9px', px: 1.6, py: 1.2 }}>
                            {[['Gross salary', gross, '#16A34A'], ['Total deductions', totalDed, '#DC2626'], ['Net pay', net, PRIMARY_DARK]]
                                .filter(([, v]) => v != null && isNum(v))
                                .map(([label, v, color], i) => (
                                    <Stack key={label} direction="row" sx={{ justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography sx={{ fontSize: i === 2 ? 13.5 : 12.5, fontWeight: i === 2 ? 800 : 600, color: '#374151' }}>{label}</Typography>
                                        <Typography sx={{ fontSize: i === 2 ? 15 : 13, fontWeight: 800, color }}>{inr(v)}</Typography>
                                    </Stack>
                                ))}
                        </Box>
                    </>
                )}
            </Box>
        </Drawer>
    );
}
